import React, { useMemo, useRef, useLayoutEffect, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import AudioPlayer from './AudioPlayer';
import { wrapResponsiveEmbeds, EMBED_RESPONSIVE_STYLE, EMBED_IFRAME_STYLE } from '../utils/embedResponsive';
import { parseHtmlSegments } from '../utils/parseHtmlSegments';
import { useResolvedMediaHtml } from '../utils/useResolvedMediaHtml';
import db from '../services/db';
import { ImageLightboxPortal, setupExpandableImages, useImageLightbox } from './ExpandableImageLightbox';
import './RichTextSlideRenderer.css';

const IMAGE_RETRY_DELAYS_MS = [2000, 5000];

function hasRichMediaWidgets(html) {
    return /audio-player-wrapper|video-player-wrapper|<audio[\s>]|<iframe[\s>]/i.test(html || '');
}

async function retryImageLoad(img) {
    if (img.complete && img.naturalWidth > 0) return;

    const tries = Number(img.dataset.mediaRetries || '0');
    if (tries >= IMAGE_RETRY_DELAYS_MS.length) {
        img.classList.add('embedded-image--failed');
        img.alt = img.alt || 'Image failed to load';
        return;
    }

    img.dataset.mediaRetries = String(tries + 1);
    await new Promise((resolve) => setTimeout(resolve, IMAGE_RETRY_DELAYS_MS[tries]));

    if (img.complete && img.naturalWidth > 0) return;

    try {
        const fresh = await db.getSignedMediaUrl(img.src, { bypassCache: true });
        if (fresh && fresh !== img.src) img.src = fresh;
    } catch {
        /* wait for next error event if load still fails */
    }
}

function bindBrokenMediaHandlers(container) {
    if (!container) return;
    container.querySelectorAll('img').forEach((img) => {
        if (img.dataset.mediaErrorBound) return;
        img.dataset.mediaErrorBound = '1';
        img.addEventListener('error', () => {
            retryImageLoad(img);
        });
    });
}

function mountRichMediaWidgets(container, rootsRef) {
    if (!container || !hasRichMediaWidgets(container.innerHTML)) return;

    container.querySelectorAll('.audio-player-wrapper').forEach((w) => {
        const src = w.getAttribute('data-audio-src');
        if (src) {
            w.innerHTML = '';
            const r = createRoot(w);
            r.render(<AudioPlayer src={src} />);
            rootsRef.current.push(r);
        }
    });

    container.querySelectorAll('audio[controls]').forEach((el) => {
        const src = el.getAttribute('src');
        if (src) {
            const d = document.createElement('div');
            d.style.cssText = 'width:100%;max-width:100%;margin:1rem 0';
            el.parentNode?.replaceChild(d, el);
            const r = createRoot(d);
            r.render(<AudioPlayer src={src} />);
            rootsRef.current.push(r);
        }
    });

    container.querySelectorAll('.video-player-wrapper').forEach((w) => {
        const src = w.getAttribute('data-video-src');
        if (src) {
            const d = document.createElement('div');
            d.className = 'embed-responsive';
            d.style.cssText = 'width:100%;max-width:100%;margin:1rem 0;position:relative;padding-bottom:56.25%;height:0;overflow:hidden';
            const iframe = document.createElement('iframe');
            iframe.src = src;
            iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px';
            iframe.allow = 'autoplay; fullscreen; picture-in-picture';
            iframe.allowFullScreen = true;
            iframe.title = 'Embedded video';
            d.appendChild(iframe);
            w.parentNode?.replaceChild(d, w);
        }
    });

    wrapResponsiveEmbeds(container);
}

function HtmlSegment({ content }) {
    const ref = useRef(null);

    useLayoutEffect(() => {
        if (ref.current) {
            wrapResponsiveEmbeds(ref.current);
            bindBrokenMediaHandlers(ref.current);
        }
    }, [content]);

    return (
        <div
            ref={ref}
            className="rich-text-content"
            dangerouslySetInnerHTML={{ __html: content }}
        />
    );
}

/**
 * Generic HTML renderer that replaces audio elements with custom AudioPlayer components.
 */
const RichTextSlideRenderer = ({ content, style = {} }) => {
    const resolvedContent = useResolvedMediaHtml(content || '');
    const segments = useMemo(() => parseHtmlSegments(resolvedContent), [resolvedContent]);
    const hostRef = useRef(null);
    const { expandedSrc, openImage, closeImage } = useImageLightbox();

    useLayoutEffect(() => {
        const el = hostRef.current;
        if (!el) return undefined;
        return setupExpandableImages(el, openImage);
    }, [resolvedContent, openImage]);

    return (
        <div ref={hostRef} className="rich-text-content" style={style}>
            {segments.map((segment, index) => (
                segment.type === 'html' ? (
                    <HtmlSegment key={`html-${index}`} content={segment.content} />
                ) : segment.type === 'video' ? (
                    <div key={`video-${segment.id}`} className="embed-responsive" style={EMBED_RESPONSIVE_STYLE}>
                        <iframe
                            src={segment.src}
                            style={EMBED_IFRAME_STYLE}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title="Embedded video"
                        />
                    </div>
                ) : (
                    <div key={`audio-${segment.id}`} style={{ width: '100%', margin: '1rem 0' }}>
                        <AudioPlayer src={segment.src} />
                    </div>
                )
            ))}
            <ImageLightboxPortal src={expandedSrc} onClose={closeImage} />
        </div>
    );
};

/**
 * Inline HTML with audio/video widgets.
 * React 19 re-applying dangerouslySetInnerHTML on parent updates would wipe the
 * custom AudioPlayer and flash the native <audio controls> at 0:00. This node
 * owns innerHTML and only remounts widgets when the HTML string actually changes.
 */
export const InlineAudioRenderer = React.memo(function InlineAudioRenderer({ html, style = {}, inline = false, ...domProps }) {
    const containerRef = useRef(null);
    const rootsRef = useRef([]);
    const appliedHtmlRef = useRef(null);
    const resolvedHtml = useResolvedMediaHtml(html || '');
    const { expandedSrc, openImage, closeImage } = useImageLightbox();

    useLayoutEffect(() => {
        const c = containerRef.current;
        if (!c) return;
        if (appliedHtmlRef.current === resolvedHtml) return;
        appliedHtmlRef.current = resolvedHtml;
        rootsRef.current.forEach((r) => {
            try { r.unmount(); } catch { /* already unmounted */ }
        });
        rootsRef.current = [];
        c.innerHTML = resolvedHtml || '';
        mountRichMediaWidgets(c, rootsRef);
        bindBrokenMediaHandlers(c);
    }, [resolvedHtml]);

    useEffect(() => {
        const c = containerRef.current;
        if (!c) return undefined;
        return setupExpandableImages(c, openImage);
    }, [resolvedHtml, openImage]);

    useEffect(() => () => {
        rootsRef.current.forEach((r) => {
            try { r.unmount(); } catch { /* already unmounted */ }
        });
        rootsRef.current = [];
    }, []);

    return (
        <>
            <span
                ref={containerRef}
                className="rich-text-content"
                {...domProps}
                style={{ display: inline ? 'inline' : 'block', maxWidth: '100%', ...style }}
            />
            <ImageLightboxPortal src={expandedSrc} onClose={closeImage} />
        </>
    );
}, (prev, next) => prev.html === next.html && prev.inline === next.inline);

export default RichTextSlideRenderer;
